import { BrowserRouter, Route, Routes } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@mui/material';
import { ToastContainer } from 'react-toastify';

import { UserPermissions } from '@models/user';
import DashboardLayout from '@layouts/DashboardLayout';
import PrivateRoute from '@routes/PrivateRoute';
import ConfirmDialogProvider from '@components/ui/ConfirmDialog';

import LoginPage from '@pages/LoginPage';
import FlowsPage from '@pages/FlowsPage';
import FlowEditPage from '@pages/FlowEditPage';
import UsersPage from '@pages/UsersPage';
import UserEditPage from '@pages/UserEditPage';
import ProfilePage from '@pages/ProfilePage';
import NotFoundPage from '@pages/NotFoundPage';

import { theme } from 'theme';
import '@styles/index.css';

import { queryClient } from '@api/queryClient';
import SettingsPage from '@pages/SettingPage';
import SettingEditPage from '@pages/SettingEditPage';
import IntermediaryDataPage from '@pages/IntermediaryPage/intermediary-analytics-page';
import PaymentsPage from '@pages/PaymentsPage';
import CustomersPage from '@pages/CustomersPage';
import IncidentsPage from '@pages/IncidentsPage';
import IncidentDetailPage from '@pages/IncidentDetailPage';

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <ConfirmDialogProvider>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LoginPage />} />

              {/* Private routes */}
              <Route element={<PrivateRoute />}>
                <Route path="/dashboard" element={<DashboardLayout />}>
                  <Route path="/dashboard/charger-flows" element={<FlowsPage />} />
                  <Route path="/dashboard/charger-flows/:id/edit" element={<FlowEditPage />} />
                  <Route element={<PrivateRoute permission={UserPermissions.VIEW_USERS} />}>
                    <Route path="/dashboard/users" element={<UsersPage />} />
                    <Route path="/dashboard/users/:id/edit" element={<UserEditPage />} />
                  </Route>
                  <Route path="/dashboard/profile" element={<ProfilePage />} />
                  <Route path="/dashboard/settings" element={<SettingsPage />} />
                  <Route path="/dashboard/settings/:id/edit" element={<SettingEditPage />} />
                  <Route path="/dashboard/intermediary-data" element={<IntermediaryDataPage />} />
                  <Route path="/dashboard/payments" element={<PaymentsPage />} />
                  <Route path="/dashboard/customers" element={<CustomersPage />} />
                  <Route path="/dashboard/customers/:customerId" element={<CustomersPage />} />
                  <Route path="/dashboard/incidents" element={<IncidentsPage />} />
                  <Route path="/dashboard/incidents/:incidentId" element={<IncidentDetailPage />} />
                </Route>
              </Route>

              {/* Fallback - 404 page */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </ConfirmDialogProvider>
        </ThemeProvider>
        <ToastContainer />
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
